import {
  Flex,
  SimpleGrid,
  Text,
  Tooltip,
  useMantineTheme,
} from "@mantine/core";
import { useFormContext } from "react-hook-form";
import {
  IMaskInputControlled,
  SegmentControlRounded,
  TextInputControlled,
  SectionsForm,
} from "../../components";
import { useEffect } from "react";
import { useAddressByCEP } from "../../hooks/useCEP";
import { IPostBuyPlan } from "../../interfaces/Purchase";
import { useUser } from "../../hooks/useUsers";
import { useTranslation } from "react-i18next";
interface IProps {
  handleChangeUser: (e: any) => void;
  isNewUser: boolean;
}

const PurchaseStep1 = ({ handleChangeUser, isNewUser }: IProps) => {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const {
    watch,
    setValue,
    setError,
    formState: { errors },
  } = useFormContext<IPostBuyPlan>();

  const hasZipCode = watch("payment_owner.address.zip_code").length === 9;
  const hasErrorZipCode = errors?.payment_owner?.address?.zip_code?.message;
  const { data: dataUser } = useUser({ enable: true });

  const optionsOne = [
    {
      label: t("marketplace.comprar_serviços.purchase_step_1.user"),
      value: "true",
    },
    {
      label: t("marketplace.comprar_serviços.purchase_step_1.other_buyer"),
      value: "false",
    },
  ];

  const { data: dataCep, isError: isErrorCep } = useAddressByCEP({
    cep: watch("payment_owner.address.zip_code"),
    enable: !!dataUser?.address?.cep,
  });

  useEffect(() => {
    if (!hasZipCode || isErrorCep) {
      setValue("payment_owner.address.street", "");
      setValue("payment_owner.address.neighborhood", "");
      setValue("payment_owner.address.city", "");
      setValue("payment_owner.address.state", "");
      setValue("payment_owner.address.country", "BR");
      setValue("payment_owner.address.additional_address", "");
      setValue("payment_owner.address.number", "");
      return;
    }
    if (
      !!dataCep &&
      dataUser?.address?.cep !== watch("payment_owner.address.zip_code")
    ) {
      setValue("payment_owner.address.street", dataCep?.street || "");
      setValue(
        "payment_owner.address.neighborhood",
        dataCep?.neighborhood || ""
      );
      setValue("payment_owner.address.city", dataCep?.city || "");
      setValue("payment_owner.address.state", dataCep?.state || "");
      setValue("payment_owner.address.country", "BR");
      setValue(
        "payment_owner.address.additional_address",
        dataUser?.address?.additional_address || ""
      );
      setValue("payment_owner.address.number", dataUser?.address?.number || "");
      return;
    }
    if (dataUser?.address?.cep === watch("payment_owner.address.zip_code")) {
      setValue(
        "payment_owner.address.street",
        dataUser?.address?.street || dataCep?.street || ""
      );
      setValue(
        "payment_owner.address.neighborhood",
        dataUser?.address?.neighborhood || dataCep?.neighborhood || ""
      );
      setValue(
        "payment_owner.address.city",
        dataUser?.address?.city || dataCep?.city || ""
      );
      setValue(
        "payment_owner.address.state",
        dataUser?.address?.state || dataCep?.state || ""
      );
      setValue("payment_owner.address.country", "BR");
      setValue(
        "payment_owner.address.additional_address",
        dataUser?.address?.additional_address || ""
      );
      setValue("payment_owner.address.number", dataUser?.address?.number || "");
      return;
    }

    if (isErrorCep) {
      setError("payment_owner.address.zip_code", {
        message: t("marketplace.comprar_serviços.purchase_step_1.erro_cep"),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataCep, hasZipCode, isErrorCep]);

  const styleSpecialDisabled = {
    input: {
      ":disabled": {
        backgroundColor: "white",
        color: "black",
        cursor: "not-allowed",
      },
    },
    wrapper: {
      ":disabled": {
        backgroundColor: "white",
        color: "black",
        cursor: "not-allowed",
      },
    },
  };
  return (
    <SectionsForm
      title={t("marketplace.comprar_serviços.purchase_step_1.title_section")}
      number={"01"}
    >
      <Flex direction="column" gap={"24px"}>
        <Flex w={"100%"} justify={"space-between"}>
          <Text
            fz={{ base: 24 }}
            fw={{ base: 600 }}
            lh={{ base: "37.2px" }}
            c={"basic.5"}
          >
            {t("marketplace.comprar_serviços.purchase_step_1.title")}
          </Text>
          <SegmentControlRounded
            onChange={handleChangeUser}
            data={optionsOne}
            label={t(
              "marketplace.comprar_serviços.purchase_step_1.label_switch"
            )}
          />
        </Flex>

        <SimpleGrid
          cols={1}
          breakpoints={[{ minWidth: "991", cols: 2, spacing: "32px" }]}
        >
          <TextInputControlled
            name="payment_owner.name"
            label={t("global.input.name.label")}
            placeholder={t("global.input.name.placeholder")}
            disabled={!isNewUser}
            withAsterisk
          />
          <IMaskInputControlled
            name="payment_owner.document_nr"
            label={t("global.input.document_nr.label")}
            placeholder="000.000.000-00"
            mask="000.000.000-00"
            disabled={!isNewUser}
            withAsterisk
          />
        </SimpleGrid>

        <Text
          fw={600}
          size={24}
          color={theme.colors.basic[5]}
          mt="24px"
          mb="12px"
        >
          {t("marketplace.comprar_serviços.purchase_step_1.title_address")}
        </Text>

        <SimpleGrid
          cols={1}
          breakpoints={[
            { minWidth: "991", cols: 2, spacing: "32px" },
            { minWidth: "1600", cols: 3, spacing: "32px" },
          ]}
        >
          <IMaskInputControlled
            name="payment_owner.address.zip_code"
            label={t("global.input.cep.label")}
            placeholder="00000-000"
            mask="00000-000"
            withAsterisk
          />
          <Tooltip
            label={t("global.input.cep.tooltip")}
            withArrow
            position="top-start"
          >
            <span>
              <TextInputControlled
                name="payment_owner.address.state"
                label={t("global.input.state.label")}
                placeholder={
                  isErrorCep
                    ? t("global.input.cep.placeholder_error_inputs")
                    : t("global.input.cep.placeholder_other_inputs")
                }
                withAsterisk
                disabled={true}
                width={"100%"}
                sx={
                  !isErrorCep && !hasErrorZipCode && hasZipCode
                    ? styleSpecialDisabled
                    : {}
                }
              />
            </span>
          </Tooltip>
          <Tooltip
            label={t("global.input.cep.tooltip")}
            withArrow
            position="top-start"
          >
            <span>
              <TextInputControlled
                name="payment_owner.address.city"
                label={t("global.input.city.label")}
                placeholder={
                  isErrorCep
                    ? t("global.input.cep.placeholder_error_inputs")
                    : t("global.input.cep.placeholder_other_inputs")
                }
                withAsterisk
                disabled={true}
                width={"100%"}
                sx={
                  !isErrorCep && !hasErrorZipCode && hasZipCode
                    ? styleSpecialDisabled
                    : {}
                }
              />
            </span>
          </Tooltip>

          <TextInputControlled
            name="payment_owner.address.neighborhood"
            label={t("global.input.neighborhood.label")}
            placeholder={
              isErrorCep
                ? t("global.input.cep.placeholder_error_inputs")
                : hasZipCode
                ? t("global.input.neighborhood.placeholder")
                : t("global.input.cep.placeholder_other_inputs")
            }
            withAsterisk
            disabled={isErrorCep || !hasZipCode}
            width={"100%"}
          />

          <TextInputControlled
            name="payment_owner.address.street"
            label={t("global.input.street.label")}
            placeholder={
              isErrorCep
                ? t("global.input.cep.placeholder_error_inputs")
                : hasZipCode
                ? t("global.input.street.placeholder")
                : t("global.input.cep.placeholder_other_inputs")
            }
            withAsterisk
            disabled={isErrorCep || !hasZipCode}
          />
          <TextInputControlled
            name="payment_owner.address.number"
            label={t("global.input.number.label")}
            withAsterisk
            placeholder={
              isErrorCep
                ? t("global.input.cep.placeholder_error_inputs")
                : hasZipCode
                ? t("global.input.number.placeholder")
                : t("global.input.cep.placeholder_other_inputs")
            }
            disabled={isErrorCep || !hasZipCode}
          />
          <TextInputControlled
            name="payment_owner.address.additional_address"
            label={t("global.input.additional_address.label")}
            placeholder={
              isErrorCep
                ? t("global.input.cep.placeholder_error_inputs")
                : hasZipCode
                ? t("global.input.additional_address.placeholder")
                : t("global.input.cep.placeholder_other_inputs")
            }
            disabled={isErrorCep || !hasZipCode}
          />
        </SimpleGrid>
      </Flex>
    </SectionsForm>
  );
};
export default PurchaseStep1;
